/**
 * A reusable section component ↔ its `sketch.reusable_sections` row.
 *
 * The contract between the client's library (`src/store/reusable-sections.js`)
 * and the table added in `20260827120000_reusable_sections.sql`. Kept apart
 * from both so it can be driven without a database and without a browser —
 * every rule the table enforces is checked here too, so a component that would
 * be rejected by a `CHECK` fails locally with a reason instead of failing at
 * the far end of a sync with a Postgres error string.
 *
 * ONE ROW PER COMPONENT, not one blob per designer (ADR 0100): per-component
 * conflict resolution, and one unreadable component cannot take the library.
 *
 * The row is scoped to the DESIGNER (`owner_id`), with no project hop, because
 * a reusable section belongs to no room — that is the whole reason the table
 * exists rather than a corner of `rooms.client_state`.
 */

/** Fields recording WHERE a section was, which a reusable copy must not carry. */
const ORIGIN_FIELDS = ['id', 'wallId', 'stackOnId', 'fromBucket'];

/** The table's own limit, mirrored so a too-long name fails before the wire. */
const MAX_NAME = 80;

/**
 * Component → row.
 *
 * @param {{id: string, name: string, section: object, deletedAt?: string|null}} component
 * @param {{ownerId: string}} context
 * @returns {{ok: true, row: object} | {ok: false, reason: string}}
 */
export function componentToRow(component, { ownerId } = {}) {
  if (!ownerId) return fail('A signed-in designer is required.');
  if (!component?.id) return fail('The component has no id.');

  const name = String(component.name ?? '').trim();
  if (!name) return fail('The component has no name.');
  if (name.length > MAX_NAME) return fail(`The name is longer than ${MAX_NAME} characters.`);

  const section = component.section;
  if (!section || typeof section !== 'object' || Array.isArray(section)) {
    return fail('The component has no section body.');
  }
  // The table requires it (`check (section ? 'typeCode')`) because a component
  // with no type cannot be placed on a wall, so it is not a component.
  if (!String(section.typeCode ?? '').trim()) return fail('The component has no typeCode.');

  // Strip the origin fields defensively. The library already does this on save,
  // but a row that carried a stale `wallId` to another device would place a
  // section claiming to live on a wall that device has never seen.
  const body = { ...section };
  for (const field of ORIGIN_FIELDS) delete body[field];

  return {
    ok: true,
    row: {
      id: component.id,
      owner_id: ownerId,
      name,
      section: body,
      deleted_at: component.deletedAt ?? null,
    },
  };
}

/**
 * Row → component, or null if the row cannot be used.
 *
 * Returns null rather than throwing: a pull processes many rows, and one bad
 * row must cost the designer that row and no others.
 */
export function rowToComponent(row) {
  if (!row?.id) return null;
  const name = String(row.name ?? '').trim();
  const section = row.section;
  if (!name) return null;
  if (!section || typeof section !== 'object' || Array.isArray(section)) return null;
  if (!String(section.typeCode ?? '').trim()) return null;

  const body = { ...section };
  for (const field of ORIGIN_FIELDS) delete body[field];

  return {
    id: row.id,
    name: name.slice(0, MAX_NAME),
    section: body,
    updatedAt: row.updated_at ?? null,
    deletedAt: row.deleted_at ?? null,
  };
}

/**
 * Whether two components in the same library would collide on the table's
 * per-designer name uniqueness — case- and surrounding-space-insensitive,
 * matching `lower(btrim(name))` in the constraint.
 *
 * Exported so the client can say "you already have one called that" instead of
 * letting the insert fail with a constraint name a designer cannot read.
 */
export function sameLibraryName(a, b) {
  return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
}

function fail(reason) { return { ok: false, reason }; }
